import { ConnectButton } from '@rainbow-me/rainbowkit';
import { useConnection } from 'wagmi';

import { useReferralInvitees } from '@/app/components/ReferralPageComponents/hooks/useReferralInvitees';
import ReferralEmptyState from '@/app/components/ReferralPageComponents/ReferralEmptyState';
import {
  formatActivityDate,
  formatEarned,
  formatLastUpdated,
} from '@/app/components/ReferralPageComponents/referralFormatters';
import { DASH } from '@/app/shared/constants/common';
import { useScreenWidth } from '@/app/shared/hooks/useScreenWidth';
import { cn } from '@/app/shared/lib/cn';
import { shortenAddress } from '@/app/shared/lib/formatters';
import { ReferralInviteeRow } from '@/app/shared/services/api/types';
import { Button } from '@/app/shared/ui/Button';
import PaginatedList from '@/app/shared/ui/PaginatedList';
import { Skeleton } from '@/app/shared/ui/Skeleton';

const DESKTOP_TABLE_MIN_WIDTH = 768;

const HEADERS = [
  { id: 'wallet', label: 'Invitee Wallet' },
  { id: 'joined', label: 'Joined' },
  { id: 'referralRate', label: 'Referral Rate (%)' },
  { id: 'youEarned', label: 'You earned' },
  { id: 'status', label: 'Status' },
] as const;

const desktopGrid = 'md:grid md:grid-cols-[1.3fr_1fr_1fr_1fr_1.35fr] md:items-center md:gap-4';

const STATUS_CONFIG = {
  active: {
    label: 'Active',
    dotColor: 'bg-feedback-green-100',
    textColor: 'text-feedback-green-100',
  },
  not_deposited: {
    label: 'No deposit yet',
    dotColor: 'bg-brand-1100',
    textColor: 'text-brand-1100',
  },
} as const;

interface InvitedUserRowViewProps {
  row: ReferralInviteeRow;
  explorerUrl?: string;
}

const InvitedUserRowView = ({ row, explorerUrl }: InvitedUserRowViewProps) => {
  const status = STATUS_CONFIG[row.status] ?? STATUS_CONFIG.not_deposited;
  const addressUrl = explorerUrl ? `${explorerUrl}/address/${row.address}` : undefined;
  const rateLabel = row.referralRatePct == null ? DASH : `${row.referralRatePct}%`;

  const walletValue = addressUrl ? (
    <a
      href={addressUrl}
      target='_blank'
      rel='noopener noreferrer'
      className='hover:text-gray-300'
      onClick={(event) => event.stopPropagation()}
    >
      {shortenAddress(row.address)}
    </a>
  ) : (
    <span>{shortenAddress(row.address)}</span>
  );

  return (
    <div
      className={cn(
        'flex flex-col gap-4 rounded-md bg-elevated-card-2 px-4 py-6 font-normal',
        'md:h-17 md:items-center md:overflow-hidden',
        desktopGrid,
      )}
    >
      <div className='grid min-w-0 grid-cols-2 gap-y-5 md:contents'>
        <div className='flex min-w-0 flex-col gap-2 md:gap-1'>
          <span className='text-xs font-medium tracking-wider text-gray-500 md:hidden'>Invitee Wallet</span>
          <div className='truncate text-sm text-gray-200'>{walletValue}</div>
        </div>

        <div className='flex min-w-0 flex-col gap-2 md:gap-1'>
          <span className='text-xs font-medium tracking-wider text-gray-500 md:hidden'>Joined</span>
          <div className='text-sm text-gray-200'>{formatActivityDate(row.joinedAt)}</div>
        </div>

        <div className='flex min-w-0 flex-col gap-2 md:gap-1'>
          <span className='text-xs font-medium tracking-wider text-gray-500 md:hidden'>Referral Rate (%)</span>
          <div className='text-sm text-gray-200'>{rateLabel}</div>
        </div>

        <div className='flex min-w-0 flex-col gap-2 md:gap-1'>
          <span className='text-xs font-medium tracking-wider text-gray-500 md:hidden'>You earned</span>
          <div className='text-sm text-gray-200'>{formatEarned(row.earned)}</div>
        </div>

        <div className='col-span-2 flex min-w-0 flex-col gap-2 md:col-span-1 md:gap-1'>
          <span className='text-xs font-medium tracking-wider text-gray-500 md:hidden'>Status</span>
          <div className={cn('flex items-center gap-2 text-sm', status.textColor)}>
            <span className={cn('h-1 w-1 shrink-0 rounded-full', status.dotColor)} />
            {status.label}
          </div>
        </div>
      </div>
    </div>
  );
};

const ReferralInvitedUsers = () => {
  const { isConnected, chain } = useConnection();
  const { rows, updatedAt, isLoading } = useReferralInvitees();
  const screenWidth = useScreenWidth();
  const explorerUrl = chain?.blockExplorers?.default?.url;
  const hasRows = rows.length > 0;
  const isDesktopTable = screenWidth >= DESKTOP_TABLE_MIN_WIDTH;
  const itemsPerPage = isDesktopTable ? 5 : 2;
  const lastUpdatedLabel =
    hasRows && updatedAt != null && updatedAt !== '' ? formatLastUpdated(updatedAt) : null;
  const showLastUpdatedOnTop = Boolean(lastUpdatedLabel) && !isDesktopTable;

  return (
    <div className='flex w-full flex-col gap-4'>
      <div className='flex flex-col gap-1'>
        <h2 className='text-base font-medium text-brand-1100'>Your Invited Users</h2>
        {showLastUpdatedOnTop ? (
          <p className='text-sm text-modal-result-body'>{lastUpdatedLabel}</p>
        ) : null}
      </div>

      <div className='flex flex-col'>
        <div className={cn('hidden px-4 py-3 md:grid', desktopGrid)}>
          {HEADERS.map((header) => (
            <div key={header.id} className='text-sm text-gray-500'>
              {header.label}
            </div>
          ))}
        </div>

        {!isConnected ? (
          <div className='flex min-h-76 flex-col items-center justify-center gap-5 rounded-md bg-elevated-card-2 p-4'>
            <div className='flex flex-col items-center gap-1.5'>
              <span className='leading-5.5 font-medium text-brand-1100'>Your referral stats</span>
              <p className='text-center text-sm leading-5 text-gray-400'>
                Connect your wallet to view your active
                <br />
                boosts, links, and referral earnings.
              </p>
            </div>
            <ConnectButton.Custom>
              {({ openConnectModal }) => (
                <Button
                  variant='ghost'
                  onClick={openConnectModal}
                  className='h-12 w-47.5 rounded-md bg-action-button px-5 text-base leading-6 font-medium text-white hover:bg-action-button/90'
                >
                  Connect Wallet
                </Button>
              )}
            </ConnectButton.Custom>
          </div>
        ) : isLoading ? (
          <div className='flex flex-col gap-1.5'>
            {Array.from({ length: itemsPerPage }, (_, index) => (
              <Skeleton.Line key={index} loading className={isDesktopTable ? 'h-17' : 'h-40'} />
            ))}
          </div>
        ) : !hasRows ? (
          <ReferralEmptyState
            title='No Active Boosts or Referrals'
            description='Your active boosts and referral stats will show up here as soon as they become available.'
            descriptionClassName='max-w-[255px]'
          />
        ) : (
          <PaginatedList
            key={itemsPerPage}
            itemsPerPage={itemsPerPage}
            className='flex flex-col gap-1.5'
            afterListSlot={
              lastUpdatedLabel && isDesktopTable ? (
                <div className='mt-4 text-sm text-modal-result-body'>{lastUpdatedLabel}</div>
              ) : null
            }
          >
            {rows.map((row) => (
              <InvitedUserRowView key={`${row.address}-${row.joinedAt}`} row={row} explorerUrl={explorerUrl} />
            ))}
          </PaginatedList>
        )}
      </div>
    </div>
  );
};

export default ReferralInvitedUsers;
